import { getDeckDefinition, DECKS } from '../data/decks';
import type { DeckCardDefinition, DeckDefinition } from '../data/decks';
import { DeckKey, TarotCardData } from '../types/tarot';

const deckIdToKeyMap: Record<string, DeckKey> = Object.values(DECKS).reduce(
  (acc, deck) => {
    acc[deck.deckId] = deck.key;
    return acc;
  },
  {} as Record<string, DeckKey>,
);

const createCardId = (deckId: string, cardNumber: number) => `${deckId}${cardNumber.toString().padStart(3, '0')}`;

const getCardImagePath = (deck: DeckDefinition, card: DeckCardDefinition, index: number) => {
  if (card.filename) {
    if (card.filename.startsWith('/')) {
      return card.filename;
    }
    return `/assets/cards/${deck.deckId}/${card.filename}`;
  }

  const paddedCardId = (index + 1).toString().padStart(3, '0');
  const extension = deck.imageExtension ?? 'png';
  return `/assets/cards/${deck.deckId}/${deck.deckId}${paddedCardId}.${extension}`;
};

const fallbackReversedMeaning = (meaning: string) =>
  `When reversed, this energy turns inward or feels blocked. Reflect on how "${meaning}" might be manifesting in reverse.`;

const createBaseCardData = (
  deck: DeckDefinition,
  card: DeckCardDefinition,
  index: number,
): TarotCardData => {
  const uprightMeaning = card.meaning;
  const reversedMeaning = card.reversedMeaning ?? fallbackReversedMeaning(card.meaning);
  const uprightInsight = card.insight ?? uprightMeaning;
  const reversedInsight = card.reversedInsight ?? card.insight ?? reversedMeaning;

  return {
    id: createCardId(deck.deckId, index + 1),
    deckKey: deck.key,
    name: card.name,
    image: getCardImagePath(deck, card, index),
    backImage: deck.backImage,
    orientation: 'upright',
    meaning: uprightMeaning,
    uprightMeaning,
    reversedMeaning,
    cryptoInsight: uprightInsight,
    reversedCryptoInsight: reversedInsight,
    arcana: card.arcana,
    suit: card.suit ?? null,
    number: typeof card.number === 'number' ? card.number : null,
  };
};

const applyOrientation = (card: TarotCardData, orientation: 'upright' | 'reversed'): TarotCardData => ({
  ...card,
  orientation,
  meaning: orientation === 'reversed' ? card.reversedMeaning : card.uprightMeaning,
  cryptoInsight:
    orientation === 'reversed'
      ? card.reversedCryptoInsight ?? card.reversedMeaning
      : card.cryptoInsight ?? card.uprightMeaning,
});

const chooseOrientation = (deck: DeckDefinition): 'upright' | 'reversed' => {
  const probability = typeof deck.reversedProbability === 'number' ? deck.reversedProbability : 0.4;
  return Math.random() < probability ? 'reversed' : 'upright';
};

const buildDeckCards = (deckKey: DeckKey): TarotCardData[] => {
  const deck = getDeckDefinition(deckKey);
  return deck.cards.map((card, index) => createBaseCardData(deck, card, index));
};

/**
 * Generate random cards for daily selection
 * Cards are random for each player and can change during the day
 * Only opened cards remain stable
 */
export const generateDailyCards = (deckKey: DeckKey = 'crypto'): TarotCardData[] => {
  const deck = getDeckDefinition(deckKey);
  const allCards = buildDeckCards(deckKey);
  const shuffled = [...allCards].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 8).map((card) => applyOrientation(card, chooseOrientation(deck)));
};

export const getRandomCard = (deckKey: DeckKey = 'crypto'): TarotCardData => {
  const deck = getDeckDefinition(deckKey);
  const allCards = buildDeckCards(deckKey);
  const baseCard = allCards[Math.floor(Math.random() * allCards.length)];
  return applyOrientation(baseCard, chooseOrientation(deck));
};

export const getCardById = (id: string): TarotCardData | undefined => {
  if (!id || id.length < 2) return undefined;
  const deckId = id.slice(0, 2);
  const deckKey = deckIdToKeyMap[deckId] ?? 'crypto';
  const cardNumber = parseInt(id.slice(2), 10);
  if (Number.isNaN(cardNumber)) return undefined;

  const deck = getDeckDefinition(deckKey);
  const cardDefinition = deck.cards[cardNumber - 1];
  if (!cardDefinition) return undefined;

  const baseCard = createBaseCardData(deck, cardDefinition, cardNumber - 1);
  return { ...baseCard };
};

export const getAllCards = (deckKey: DeckKey = 'crypto'): TarotCardData[] => buildDeckCards(deckKey);
